import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Button from '../../../components/common/Button/Button';
import Card from '../../../components/common/Card/Card';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import DurationPickerField from '../../../components/common/DurationPickerField/DurationPickerField';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import ExerciseThumbnailImage from '../../../components/common/ExerciseThumbnailImage/ExerciseThumbnailImage';
import Loading from '../../../components/common/Loading/Loading';
import FormInput from '../../../components/common/FormInput/FormInput';
import FormSelect, { FormSelectOption } from '../../../components/common/FormSelect/FormSelect';
import FormTextarea from '../../../components/common/FormTextarea/FormTextarea';
import Icon from '../../../components/common/Icon/Icon';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import PageContainer from '../../../components/common/PageContainer/PageContainer';
import PhotoUploadButton from '../../../components/common/PhotoUploadButton/PhotoUploadButton';
import ProfileColumns from '../../../components/common/ProfileColumns/ProfileColumns';
import ContentContainer from '../../../components/common/ContentContainer/ContentContainer';
import SectionBlock from '../../../components/common/SectionBlock/SectionBlock';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import StickyHeader from '../../../components/common/StickyHeader/StickyHeader';
import TwoColumnGrid from '../../../components/common/TwoColumnGrid/TwoColumnGrid';
import { useBreadcrumbs, BreadcrumbItem } from '../../../contexts/BreadcrumbContext';
import { useAuth } from '../../../contexts/AuthContext';
import useExercises from '../../../hooks/useExercises';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { ExerciseService, type CreateExercisePayload, type UpdateExercisePayload } from '../../../services/ExerciseService';
import { colors } from '../../../theme/colors';
import type { EffortLevel, ExerciseItem, ExerciseMethodLookup, ExerciseType, ExerciseRecord } from '../../../types/exercise';
import { canManageExercises } from '../../../utils/permissions';
import {
  DEFAULT_CONCENTRIC_SECONDS,
  DEFAULT_ECCENTRIC_SECONDS,
  DEFAULT_ISOMETRIC_SECONDS,
  DEFAULT_PAUSE_SECONDS,
  DEFAULT_REST_SECONDS,
  EXERCISE_EXECUTION_SPEED_OPTIONS,
  isCadenceExecutionSpeed,
  isIsometryExecutionSpeed,
  normalizeExecutionSpeed,
} from '../../../utils/executionSpeed';
import { getCanonicalTrainingExecutionMethodValue, getTrainingExecutionMethodDisplayLabel } from '../../../utils/trainingExecutionMethods';
import RelatedExercisePickerModal from '../components/RelatedExercisePickerModal';
import styles from './ExerciseRegister.module.css';

interface ExerciseFormValues {
  name: string;
  muscleGroupIds: string[];
  resistanceTypeId: string;
  objective: string;
  description: string;
  executionTime: string;
  restTime: string;
  heartRate: string;
  sets: string;
  repetitionsMin: string;
  repetitionsMax: string;
  method: string;
  loadPercentage: string;
  executionSpeed: string;
  concentricSeconds: string;
  pauseSeconds: string;
  eccentricSeconds: string;
  intensity: string;
  exerciseType: string;
  videoUrl: string;
}

type ExerciseFormErrors = Partial<Record<keyof ExerciseFormValues | 'thumbnail' | 'relatedExercises', string>>;

interface MethodSelectOption extends FormSelectOption {
  color?: string | null;
}

const INITIAL_FORM_VALUES: ExerciseFormValues = {
  name: '',
  muscleGroupIds: [],
  resistanceTypeId: '',
  objective: '',
  description: '',
  executionTime: '',
  restTime: '',
  heartRate: '',
  sets: '',
  repetitionsMin: '',
  repetitionsMax: '',
  method: '',
  loadPercentage: '',
  executionSpeed: '',
  concentricSeconds: '',
  pauseSeconds: '',
  eccentricSeconds: '',
  intensity: '',
  exerciseType: '',
  videoUrl: '',
};
const MAX_SMALLINT_VALUE = 32767;

const LOAD_OPTIONS: FormSelectOption[] = Array.from({ length: 10 }, (_, i) => {
  const value = String((i + 1) * 10);
  return { value, label: `${value}%` };
});

const HEART_RATE_OPTIONS: FormSelectOption[] = [
  { value: '100', label: '100 bpm' },
  { value: '120', label: '120 bpm' },
  { value: '140', label: '140 bpm' },
  { value: '160', label: '160 bpm' },
  { value: '180', label: '180 bpm' },
];

const METHOD_TYPE_LABELS_PT_BR: Record<string, string> = {
  training_structure: 'Estrutura do treino',
  intensity_methods: 'Métodos de intensidade',
  volume_progression_methods: 'Métodos de volume e progressão',
  tempo_conditioning_methods: 'Métodos de tempo e condicionamento',
};

const METHOD_TYPE_ORDER: Record<string, number> = {
  training_structure: 1,
  intensity_methods: 2,
  volume_progression_methods: 3,
  tempo_conditioning_methods: 4,
};

const normalizeMethodColor = (value?: string | null) => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return null;
  }

  const normalizedValue = trimmedValue.startsWith('#') ? trimmedValue : `#${trimmedValue}`;

  return /^#[0-9a-fA-F]{6}$/.test(normalizedValue) ? normalizedValue : null;
};

const mapMethodLookupsToOptions = (lookups: ExerciseMethodLookup[]): MethodSelectOption[] => {
  const usedMethodValues = new Set<string>();

  return [...lookups]
    .sort((a, b) => {
      const typeOrderDiff = (METHOD_TYPE_ORDER[a.type] ?? 999) - (METHOD_TYPE_ORDER[b.type] ?? 999);

      if (typeOrderDiff !== 0) {
        return typeOrderDiff;
      }

      const labelA = getTrainingExecutionMethodDisplayLabel(a.nameEn, a.name.trim() || a.nameEn?.trim() || '') || a.name.trim() || a.nameEn?.trim() || '';
      const labelB = getTrainingExecutionMethodDisplayLabel(b.nameEn, b.name.trim() || b.nameEn?.trim() || '') || b.name.trim() || b.nameEn?.trim() || '';

      return labelA.localeCompare(labelB, 'pt-BR');
    })
    .flatMap((item) => {
      const canonicalValue = getCanonicalTrainingExecutionMethodValue(item.nameEn ?? item.name);

      if (canonicalValue && usedMethodValues.has(canonicalValue)) {
        return [];
      }

      if (canonicalValue) {
        usedMethodValues.add(canonicalValue);
      }

      return [{
        value: String(item.id),
        label: getTrainingExecutionMethodDisplayLabel(canonicalValue, item.name) || item.name,
        group: METHOD_TYPE_LABELS_PT_BR[item.type] ?? item.type,
        color: normalizeMethodColor(item.color),
      }];
    });
};

const RPE_OPTIONS: FormSelectOption[] = [
  { value: 'very_light', label: '0-1 = Muito leve' },
  { value: 'light', label: '2-3 = Leve' },
  { value: 'moderate', label: '4-5 = Moderada' },
  { value: 'moderately_high', label: '6-7 = Moderadamente alta / Desconfortável, mas sustentável' },
  { value: 'high', label: '8-9 = Alta / Muito difícil' },
  { value: 'maximal', label: '10 = Esforço máximo' },
];

const EXERCISE_TYPE_OPTIONS: FormSelectOption[] = [
  { value: 'strength', label: 'Força' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'flexibility', label: 'Flexibilidade' },
];

const parseEffortLevel = (value: string): EffortLevel | null => {
  if (value === 'very_light' || value === 'light' || value === 'moderate' || value === 'moderately_high' || value === 'high' || value === 'maximal') {
    return value;
  }

  return null;
};

const parseExerciseType = (value: string): ExerciseType | null => {
  if (value === 'strength' || value === 'cardio' || value === 'flexibility') {
    return value;
  }

  return null;
};

const normalizeExerciseCode = (value: string) => {
  const normalizedValue = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  return normalizedValue || 'exercise';
};

const buildExerciseCode = (name: string) => `${normalizeExerciseCode(name)}-${Date.now().toString(36)}`;
const THUMBNAIL_INVALID_MIME_MESSAGE = 'A foto selecionada não é compatível. Envie uma imagem JPG, PNG ou WEBP.';
const serializeRelatedExerciseIds = (exercises: ExerciseItem[]) => JSON.stringify(exercises.map((exercise) => exercise.id));

const buildThumbnailUploadErrorImage = () => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="144" height="144" viewBox="0 0 144 144" fill="none">
      <defs>
        <linearGradient id="errorBg" x1="12" y1="16" x2="132" y2="136" gradientUnits="userSpaceOnUse">
          <stop stop-color="${colors.exerciseUploadErrorGradientStart}" />
          <stop offset="1" stop-color="${colors.exerciseUploadErrorGradientEnd}" />
        </linearGradient>
      </defs>
      <rect width="144" height="144" rx="18" fill="url(#errorBg)" />
      <circle cx="72" cy="56" r="26" fill="${colors.whiteOverlay14}" />
      <path d="M72 42V58" stroke="${colors.neutralWhite}" stroke-width="6" stroke-linecap="round" />
      <circle cx="72" cy="71" r="3.5" fill="${colors.neutralWhite}" />
      <text x="72" y="106" text-anchor="middle" fill="${colors.neutralWhite}" font-family="Roboto, Arial, sans-serif" font-size="12" font-weight="700">Formato inválido</text>
      <text x="72" y="122" text-anchor="middle" fill="${colors.whiteOverlay88}" font-family="Roboto, Arial, sans-serif" font-size="10">Use JPG, PNG ou WEBP</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const THUMBNAIL_UPLOAD_ERROR_PREVIEW = buildThumbnailUploadErrorImage();

const parseNullableInteger = (value: string): number | null => {
  const normalizedValue = value.replace(/[^\d-]+/g, '');

  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number.parseInt(normalizedValue, 10);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const parseNullablePositiveSmallInt = (value: string): number | null => {
  const parsedValue = parseNullableInteger(value);

  if (parsedValue === null || parsedValue <= 0 || parsedValue > MAX_SMALLINT_VALUE) {
    return null;
  }

  return parsedValue;
};

const parseIntegerList = (values: string[]): number[] => (
  values
    .map((value) => Number.parseInt(value, 10))
    .filter((value) => Number.isInteger(value) && value > 0)
);

const parseNullableDecimal = (value: string): number | null => {
  const normalizedValue = value.replace(/\s+/g, '').replace('%', '').replace(',', '.');

  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number.parseFloat(normalizedValue);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const isBodyweightResistanceType = (label: string): boolean => /peso corporal|bodyweight/i.test(label);

const resolveRelatedExerciseSubtitle = (exercise: ExerciseItem): string => {
  const objective = exercise.objective?.trim();

  if (objective && objective !== 'Sem objetivo informado.') {
    return objective;
  }

  return exercise.group;
};

const RELATED_EXERCISE_QUERY_SCHEMA = {
  relatedSearch: queryFilter.string(),
};

const ExerciseRegister: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const canManage = canManageExercises(roles);
  const isReadOnly = !canManage;
  const { exerciseId } = useParams<{ exerciseId?: string }>();
  const parsedExerciseId = exerciseId ? Number.parseInt(exerciseId, 10) : null;
  const isEditMode = Number.isInteger(parsedExerciseId);
  const { setBreadcrumbs } = useBreadcrumbs();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  const formSnapshotRef = useRef<string>(JSON.stringify(INITIAL_FORM_VALUES));
  const thumbnailSnapshotRef = useRef<string | null>(null);
  const relatedExercisesSnapshotRef = useRef<string>('[]');
  const [formValues, setFormValues] = useState<ExerciseFormValues>(INITIAL_FORM_VALUES);
  const [errors, setErrors] = useState<ExerciseFormErrors>({});
  const [muscleGroupOptions, setMuscleGroupOptions] = useState<FormSelectOption[]>([]);
  const [resistanceTypeOptions, setResistanceTypeOptions] = useState<FormSelectOption[]>([]);
  const [methodOptions, setMethodOptions] = useState<MethodSelectOption[]>([]);
  const [isLoadingLookups, setIsLoadingLookups] = useState(true);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingExercise, setIsLoadingExercise] = useState(false);
  const [currentExercise, setCurrentExercise] = useState<ExerciseRecord | null>(null);
  const [relatedExercises, setRelatedExercises] = useState<ExerciseItem[]>([]);
  const [isRelatedPickerOpen, setIsRelatedPickerOpen] = useState(false);
  const {
    filters: { relatedSearch: relatedSearchTerm },
    setFilter: setRelatedFilter,
  } = useQueryFilters(RELATED_EXERCISE_QUERY_SCHEMA);
  const [pendingRelatedExercises, setPendingRelatedExercises] = useState<ExerciseItem[]>([]);
  const [relatedExercisePendingRemoval, setRelatedExercisePendingRemoval] = useState<ExerciseItem | null>(null);
  const {
    exercises: relatedCatalogExercises,
    loading: relatedCatalogLoading,
  } = useExercises(1, 1000, {
    searchTerm: relatedSearchTerm,
    groups: formValues.muscleGroupIds,
    enabled: isRelatedPickerOpen,
  });

  useEffect(() => {
    const crumbs: BreadcrumbItem[] = [
      { label: 'Exercícios', path: '/exercicios' },
      { label: isEditMode ? 'Editar exercício' : 'Novo exercício' },
    ];

    setBreadcrumbs(crumbs);

    return () => setBreadcrumbs([]);
  }, [isEditMode, setBreadcrumbs]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadLookups = async () => {
      try {
        setIsLoadingLookups(true);

        const lookups = await ExerciseService.getExerciseLookups();

        if (!isMounted) {
          return;
        }

        setMuscleGroupOptions(lookups.muscleGroups.map((item) => ({ value: String(item.id), label: item.name })));
        setResistanceTypeOptions(lookups.resistanceTypes.map((item) => ({ value: String(item.id), label: item.name })));
        setMethodOptions(mapMethodLookupsToOptions(lookups.methods));
        setSubmitError('');
      } catch (lookupError) {
        if (!isMounted) {
          return;
        }

        setSubmitError(lookupError instanceof Error ? lookupError.message : 'Não foi possível carregar os dados do formulário.');
      } finally {
        if (isMounted) {
          setIsLoadingLookups(false);
        }
      }
    };

    loadLookups();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode || !parsedExerciseId) {
      return;
    }

    let isMounted = true;

    const loadExercise = async () => {
      try {
        setIsLoadingExercise(true);
        const { record, muscleGroupIds, relatedExercises: loadedRelatedExercises } = await ExerciseService.getExerciseEditorData(parsedExerciseId);

        if (!isMounted) {
          return;
        }

        setCurrentExercise(record);
        const loadedFormValues = ExerciseService.getFormValuesFromRecord(record, muscleGroupIds);
        setFormValues(loadedFormValues);
        setRelatedExercises(loadedRelatedExercises);
        setThumbnailPreview(null);
        setSelectedThumbnailFile(null);
        formSnapshotRef.current = JSON.stringify(loadedFormValues);
        thumbnailSnapshotRef.current = null;
        relatedExercisesSnapshotRef.current = serializeRelatedExerciseIds(loadedRelatedExercises);
        setSubmitError('');
      } catch (exerciseError) {
        if (!isMounted) {
          return;
        }

        setSubmitError(exerciseError instanceof Error ? exerciseError.message : 'Não foi possível carregar o exercício.');
      } finally {
        if (isMounted) {
          setIsLoadingExercise(false);
        }
      }
    };

    loadExercise();

    return () => {
      isMounted = false;
    };
  }, [isEditMode, parsedExerciseId]);

  const currentExerciseId = currentExercise?.id ?? null;
  const selectedMuscleGroupIdSet = useMemo(() => new Set(parseIntegerList(formValues.muscleGroupIds)), [formValues.muscleGroupIds]);
  const selectedMuscleGroupLabels = useMemo(
    () => muscleGroupOptions
      .filter((option) => formValues.muscleGroupIds.includes(option.value))
      .map((option) => option.label),
    [formValues.muscleGroupIds, muscleGroupOptions]
  );
  const methodColorByValue = useMemo(
    () => methodOptions.reduce<Record<string, string>>((accumulator, option) => {
      if (option.color) {
        accumulator[option.value] = option.color;
      }

      return accumulator;
    }, {}),
    [methodOptions]
  );

  const relatedExerciseOptions = useMemo(() => (
    relatedCatalogExercises.filter((exercise) => (
      exercise.id !== currentExerciseId
    ))
  ), [currentExerciseId, relatedCatalogExercises]);

  const invalidRelatedExercises = useMemo(
    () => relatedExercises.filter((exercise) => {
      const exerciseMuscleGroupIds = exercise.muscleGroupIds.length > 0
        ? exercise.muscleGroupIds
        : exercise.muscleGroupId
          ? [exercise.muscleGroupId]
          : [];

      return exerciseMuscleGroupIds.length === 0 || !exerciseMuscleGroupIds.some((groupId) => selectedMuscleGroupIdSet.has(groupId));
    }),
    [relatedExercises, selectedMuscleGroupIdSet]
  );

  const isBodyweightExercise = useMemo(() => {
    const selectedResistanceType = resistanceTypeOptions.find((option) => option.value === formValues.resistanceTypeId);

    return isBodyweightResistanceType(selectedResistanceType?.label ?? '');
  }, [formValues.resistanceTypeId, resistanceTypeOptions]);
  const isCardioExercise = formValues.exerciseType === 'cardio';
  const isHeartRateFieldDisabled = !isCardioExercise;
  const usesIsometrySpeed = isIsometryExecutionSpeed(formValues.executionSpeed);
  const usesCadenceSpeed = isCadenceExecutionSpeed(formValues.executionSpeed);

  const renderMethodOption = useCallback((option: FormSelectOption) => {
    const color = methodColorByValue[option.value] ?? null;

    return (
      <span className={styles.methodOptionContent}>
        {color ? <span className={styles.methodColorDot} style={{ backgroundColor: color }} aria-hidden="true" /> : null}
        <span className={styles.methodOptionLabel}>{option.label}</span>
      </span>
    );
  }, [methodColorByValue]);

  const isDirty = useMemo(() => (
    JSON.stringify(formValues) !== formSnapshotRef.current
    || thumbnailPreview !== thumbnailSnapshotRef.current
    || selectedThumbnailFile !== null
    || serializeRelatedExerciseIds(relatedExercises) !== relatedExercisesSnapshotRef.current
  ), [formValues, relatedExercises, selectedThumbnailFile, thumbnailPreview]);

  useEffect(() => {
    if (invalidRelatedExercises.length === 0) {
      setErrors((currentErrors) => {
        if (!currentErrors.relatedExercises) {
          return currentErrors;
        }

        return {
          ...currentErrors,
          relatedExercises: '',
        };
      });
    }
  }, [invalidRelatedExercises.length]);

  const handleNavigateBack = useCallback(() => {
    navigate('/exercicios');
  }, [navigate]);

  const handleFieldChange = useCallback((field: keyof ExerciseFormValues, value: string | string[]) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      [field]: '',
    }));
    setSubmitError('');
  }, []);

  const handleRestTimeChange = useCallback((value: string) => {
    handleFieldChange('restTime', value);
  }, [handleFieldChange]);

  const handleSecondsFieldChange = useCallback((
    field: 'executionTime' | 'concentricSeconds' | 'pauseSeconds' | 'eccentricSeconds',
    value: string,
  ) => {
    handleFieldChange(field, value);
  }, [handleFieldChange]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (isReadOnly) {
      return;
    }

    const { name, value } = event.target;

    if (name === 'repetitionsMin' || name === 'repetitionsMax' || name === 'executionTime' || name === 'heartRate') {
      const maxLength = name === 'heartRate' ? 3 : 5;
      const sanitizedValue = value.replace(/\D+/g, '').slice(0, maxLength);

      setFormValues((currentValues) => ({
        ...currentValues,
        [name]: sanitizedValue,
      }));
      setErrors((currentErrors) => ({
        ...currentErrors,
        repetitionsMin: '',
        repetitionsMax: '',
      }));
      setSubmitError('');
      return;
    }

    if (name === 'executionSpeed') {
      const nextSpeed = normalizeExecutionSpeed(value);

      setFormValues((currentValues) => {
        if (nextSpeed === 'isometry') {
          return {
            ...currentValues,
            executionSpeed: nextSpeed,
            concentricSeconds: '',
            pauseSeconds: '',
            eccentricSeconds: '',
          };
        }

        if (nextSpeed === 'cadence') {
          return {
            ...currentValues,
            executionSpeed: nextSpeed,
            concentricSeconds: /^(0|[1-9]\d*)$/.test(currentValues.concentricSeconds.trim())
              ? currentValues.concentricSeconds
              : String(DEFAULT_CONCENTRIC_SECONDS),
            pauseSeconds: /^(0|[1-9]\d*)$/.test(currentValues.pauseSeconds.trim())
              ? currentValues.pauseSeconds
              : String(DEFAULT_PAUSE_SECONDS),
            eccentricSeconds: /^(0|[1-9]\d*)$/.test(currentValues.eccentricSeconds.trim())
              ? currentValues.eccentricSeconds
              : String(DEFAULT_ECCENTRIC_SECONDS),
          };
        }

        return {
          ...currentValues,
          executionSpeed: '',
          concentricSeconds: '',
          pauseSeconds: '',
          eccentricSeconds: '',
        };
      });
      setErrors((currentErrors) => ({
        ...currentErrors,
        executionSpeed: '',
        executionTime: '',
        concentricSeconds: '',
        pauseSeconds: '',
        eccentricSeconds: '',
      }));
      setSubmitError('');
      return;
    }

    if (name === 'exerciseType' && value !== 'cardio') {
      setFormValues((currentValues) => ({
        ...currentValues,
        exerciseType: value,
        heartRate: '',
      }));
      setErrors((currentErrors) => ({
        ...currentErrors,
        exerciseType: '',
        heartRate: '',
      }));
      setSubmitError('');
      return;
    }

    if (name === 'resistanceTypeId') {
      const selectedResistanceType = resistanceTypeOptions.find((option) => option.value === value);
      const shouldHideLoad = isBodyweightResistanceType(selectedResistanceType?.label ?? '');

      setFormValues((currentValues) => ({
        ...currentValues,
        resistanceTypeId: value,
        loadPercentage: shouldHideLoad ? '' : currentValues.loadPercentage,
      }));
      setErrors((currentErrors) => ({
        ...currentErrors,
        resistanceTypeId: '',
        loadPercentage: '',
      }));
      setSubmitError('');
      return;
    }

    handleFieldChange(name as keyof ExerciseFormValues, value);
  }, [handleFieldChange, isReadOnly, resistanceTypeOptions]);

  const handleMuscleGroupChange = useCallback((values: string[]) => {
    if (isReadOnly) {
      return;
    }

    handleFieldChange('muscleGroupIds', values);
  }, [handleFieldChange, isReadOnly]);

  const handleThumbnailButtonClick = useCallback(() => {
    if (isReadOnly) {
      return;
    }

    fileInputRef.current?.click();
  }, [isReadOnly]);

  const handlePasteVideoUrl = useCallback(async () => {
    if (isReadOnly) {
      return;
    }

    try {
      const clipboardText = await navigator.clipboard.readText();
      const trimmedClipboardText = clipboardText.trim();

      if (!trimmedClipboardText) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          videoUrl: 'A área de transferência está vazia.',
        }));
        return;
      }

      handleFieldChange('videoUrl', trimmedClipboardText);
    } catch {
      setSubmitError('Não foi possível colar a URL do vídeo.');
    }
  }, [handleFieldChange, isReadOnly]);

  const handleThumbnailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) {
      return;
    }

    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        thumbnail: 'Selecione um arquivo de imagem válido.',
      }));
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const nextObjectUrl = URL.createObjectURL(selectedFile);
    objectUrlRef.current = nextObjectUrl;
    setSelectedThumbnailFile(selectedFile);
    setThumbnailPreview(nextObjectUrl);
    setErrors((currentErrors) => ({
      ...currentErrors,
      thumbnail: '',
    }));
  }, [isReadOnly]);

  const handleOpenRelatedPicker = useCallback(() => {
    if (isReadOnly) {
      return;
    }

    setPendingRelatedExercises(relatedExercises);
    setIsRelatedPickerOpen(true);
  }, [isReadOnly, relatedExercises]);

  const handleCloseRelatedPicker = useCallback(() => {
    setIsRelatedPickerOpen(false);
  }, []);

  const handleRelatedSearchChange = useCallback((value: string) => {
    setRelatedFilter('relatedSearch', value);
  }, [setRelatedFilter]);

  const handleTogglePendingRelated = useCallback((exercise: ExerciseItem) => {
    if (isReadOnly) {
      return;
    }

    setPendingRelatedExercises((currentExercises) => (
      currentExercises.some((item) => item.id === exercise.id)
        ? currentExercises.filter((item) => item.id !== exercise.id)
        : [...currentExercises, exercise]
    ));
  }, [isReadOnly]);

  const handleConfirmRelatedSelection = useCallback(() => {
    if (isReadOnly) {
      return;
    }

    setRelatedExercises(pendingRelatedExercises);
    setIsRelatedPickerOpen(false);
  }, [isReadOnly, pendingRelatedExercises]);

  const handleRequestRemoveRelatedExercise = useCallback((exercise: ExerciseItem) => {
    if (isReadOnly) {
      return;
    }

    setRelatedExercisePendingRemoval(exercise);
  }, [isReadOnly]);

  const handleCancelRemoveRelatedExercise = useCallback(() => {
    setRelatedExercisePendingRemoval(null);
  }, []);

  const handleConfirmRemoveRelatedExercise = useCallback((exerciseId: number) => {
    if (isReadOnly) {
      return;
    }

    setRelatedExercises((currentExercises) => currentExercises.filter((exercise) => exercise.id !== exerciseId));
    setRelatedExercisePendingRemoval(null);
  }, [isReadOnly]);

  const validateForm = useCallback(() => {
    const nextErrors: ExerciseFormErrors = {};
    const repetitionsMin = parseNullableInteger(formValues.repetitionsMin);
    const repetitionsMax = parseNullableInteger(formValues.repetitionsMax);

    if (!formValues.name.trim()) {
      nextErrors.name = 'Informe o nome do exercício.';
    }

    if (formValues.muscleGroupIds.length === 0) {
      nextErrors.muscleGroupIds = 'Selecione ao menos um grupo muscular.';
    }

    if (invalidRelatedExercises.length > 0) {
      nextErrors.relatedExercises = 'Há exercícios relacionados fora dos grupos musculares selecionados.';
    }

    if (!formValues.videoUrl.trim()) {
      nextErrors.videoUrl = 'Informe a URL do video.';
    }

    if (formValues.repetitionsMin.trim()) {
      if (repetitionsMin === null || repetitionsMin <= 0 || repetitionsMin > MAX_SMALLINT_VALUE) {
        nextErrors.repetitionsMin = 'Informe um mínimo válido entre 1 e 32767.';
      }
    }

    if (formValues.repetitionsMax.trim()) {
      if (repetitionsMax === null || repetitionsMax <= 0 || repetitionsMax > MAX_SMALLINT_VALUE) {
        nextErrors.repetitionsMax = 'Informe um máximo válido entre 1 e 32767.';
      }
    }

    if (repetitionsMin !== null && repetitionsMax !== null && repetitionsMin > repetitionsMax) {
      nextErrors.repetitionsMin = 'O mínimo não pode ser maior que o máximo.';
      nextErrors.repetitionsMax = 'O máximo não pode ser menor que o mínimo.';
    }

    if (isIsometryExecutionSpeed(formValues.executionSpeed)) {
      const isometricSeconds = parseNullableInteger(formValues.executionTime);

      if (isometricSeconds === null || isometricSeconds <= 0) {
        nextErrors.executionTime = 'Informe o tempo da execução.';
      }
    }

    if (isCadenceExecutionSpeed(formValues.executionSpeed)) {
      const concentricSeconds = parseNullableInteger(formValues.concentricSeconds);
      const pauseSeconds = parseNullableInteger(formValues.pauseSeconds);
      const eccentricSeconds = parseNullableInteger(formValues.eccentricSeconds);

      if (concentricSeconds === null || concentricSeconds < 0) {
        nextErrors.concentricSeconds = 'Informe o tempo da fase concêntrica.';
      }

      if (pauseSeconds === null || pauseSeconds < 0) {
        nextErrors.pauseSeconds = 'Informe o tempo da pausa.';
      }

      if (eccentricSeconds === null || eccentricSeconds < 0) {
        nextErrors.eccentricSeconds = 'Informe o tempo da fase excêntrica.';
      }
    }

    return nextErrors;
  }, [
    formValues.concentricSeconds,
    formValues.eccentricSeconds,
    formValues.executionSpeed,
    formValues.executionTime,
    formValues.muscleGroupIds,
    formValues.name,
    formValues.pauseSeconds,
    formValues.repetitionsMax,
    formValues.repetitionsMin,
    formValues.videoUrl,
    invalidRelatedExercises.length,
  ]);

  const buildExercisePayload = useCallback((code: string | null): CreateExercisePayload | UpdateExercisePayload => {
    const selectedEffortLevel = parseEffortLevel(formValues.intensity);
    const executionSpeed = normalizeExecutionSpeed(formValues.executionSpeed);
    const cadenceSeconds = executionSpeed === 'cadence'
      ? {
        default_concentric_seconds: parseNullableInteger(formValues.concentricSeconds) ?? DEFAULT_CONCENTRIC_SECONDS,
        default_pause_seconds: parseNullableInteger(formValues.pauseSeconds) ?? DEFAULT_PAUSE_SECONDS,
        default_eccentric_seconds: parseNullableInteger(formValues.eccentricSeconds) ?? DEFAULT_ECCENTRIC_SECONDS,
      }
      : {
        default_concentric_seconds: null,
        default_pause_seconds: null,
        default_eccentric_seconds: null,
      };

    return {
      name: formValues.name.trim(),
      code,
      muscle_group_ids: parseIntegerList(formValues.muscleGroupIds),
      resistance_type_id: parseNullableInteger(formValues.resistanceTypeId),
      instructions: formValues.description.trim() || null,
      default_sets: formValues.sets.trim() || null,
      default_repetitions_min: parseNullablePositiveSmallInt(formValues.repetitionsMin),
      default_repetitions_max: parseNullablePositiveSmallInt(formValues.repetitionsMax),
      default_rest_sec: parseNullableInteger(formValues.restTime),
      default_execution_sec: executionSpeed === 'cadence' ? null : parseNullableInteger(formValues.executionTime),
      default_load_percent: isBodyweightExercise ? null : parseNullableDecimal(formValues.loadPercentage),
      default_execution_speed: executionSpeed,
      ...cadenceSeconds,
      default_effort_level: selectedEffortLevel,
      exercise_type: parseExerciseType(formValues.exerciseType),
      default_method_id: parseNullableInteger(formValues.method),
      default_heart_rate_min: null,
      default_heart_rate_max: formValues.exerciseType === 'cardio' ? parseNullableInteger(formValues.heartRate) : null,
      objective: formValues.objective.trim() || null,
      video_url: formValues.videoUrl.trim() || null,
    };
  }, [formValues, isBodyweightExercise]);

  const handleSave = useCallback(async () => {
    if (isReadOnly) {
      return;
    }

    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError('Preencha os campos obrigatórios para salvar o exercício.');
      return;
    }

    try {
      setIsSaving(true);

      if (isEditMode && parsedExerciseId) {
        await ExerciseService.updateExercise(
          parsedExerciseId,
          buildExercisePayload(currentExercise?.code ?? buildExerciseCode(formValues.name)),
          selectedThumbnailFile,
          relatedExercises.map((exercise) => exercise.id),
        );

        window.location.href = `/exercicios/${parsedExerciseId}/editar`;
        return;
      }

      const createdExercise = await ExerciseService.createExercise(
        buildExercisePayload(buildExerciseCode(formValues.name)) as CreateExercisePayload,
        selectedThumbnailFile,
        relatedExercises.map((exercise) => exercise.id),
      );

      window.location.href = `/exercicios/${createdExercise.id}/editar`;
      return;
    } catch (serviceError) {
      const message = serviceError instanceof Error ? serviceError.message : 'Não foi possível salvar o exercício. Tente novamente.';

      if (message === THUMBNAIL_INVALID_MIME_MESSAGE) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          thumbnail: message,
        }));
        setThumbnailPreview(THUMBNAIL_UPLOAD_ERROR_PREVIEW);
      }

      setSubmitError(message);
    } finally {
      setIsSaving(false);
    }
  }, [buildExercisePayload, currentExercise?.code, formValues.name, isEditMode, isReadOnly, parsedExerciseId, relatedExercises, selectedThumbnailFile, validateForm]);

  if (isLoadingLookups || isLoadingExercise) {
    return (
      <PageContainer className={styles.container}>
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer className={styles.container}>
      <StickyHeader
        contentClassName={styles.headerContent}
        topBarClassName={styles.topBar}
        actionsClassName={styles.topBarRight}
        backButtonClassName={styles.backButton}
        saveButtonClassName={styles.saveButton}
        onBack={handleNavigateBack}
        onCancel={handleNavigateBack}
        onSave={canManage ? handleSave : undefined}
        isSaving={isSaving}
        disabled={isReadOnly || isSaving || isLoadingLookups || isLoadingExercise || !isDirty}
      />

      {submitError && <AlertBanner message={submitError} variant="error" className={styles.alert} />}
      {/* {isReadOnly && <AlertBanner message="Seu perfil possui acesso somente leitura. Apenas administradoras podem criar, editar ou excluir exercícios." variant="warning" className={styles.alert} />} */}

      <ContentContainer className={styles.contentBg}>
        <ProfileColumns className={styles.columns}>
          <div className={styles.mainColumn}>
            <section className={styles.panel}>
              <div className={styles.panelHeader}>Informações</div>
              <div className={styles.panelBody}>
                <SectionBlock spacing="margin">
                  <SectionTitle text="Informações principais" />
                  <FormInput
                    id="exercise-name"
                    name="name"
                    label="Nome do exercício"
                    required
                    value={formValues.name}
                    onChange={handleInputChange}
                    placeholder="Inserir nome do exercício"
                    error={errors.name}
                    containerClassName={styles.fullWidthField}
                    readOnly={isReadOnly}
                  />

                  <div className={styles.threeColumnGrid}>
                    <FormSelect
                      id="exercise-type"
                      name="exerciseType"
                      label="Tipo de exercício"
                      value={formValues.exerciseType}
                      onChange={handleInputChange}
                      options={EXERCISE_TYPE_OPTIONS}
                      placeholder="Selecionar"
                      disabled={isReadOnly}
                    />

                    <div className={styles.multiSelectFieldGroup}>
                      <label htmlFor="exercise-group" className={styles.multiSelectLabel}>
                        Categoria / Grupo Muscular
                        <span className={styles.multiSelectRequired}>*</span>
                      </label>
                      <MultiSelectFilter
                        values={formValues.muscleGroupIds}
                        onChange={handleMuscleGroupChange}
                        options={muscleGroupOptions}
                        placeholder={isLoadingLookups ? 'Carregando grupos...' : 'Escolher grupo muscular'}
                        className={styles.muscleGroupMultiSelectTrigger}
                        disabled={isReadOnly || isLoadingLookups}
                        hideChips
                        chipsInTrigger
                        showSelectionCount={false}
                      />
                      {errors.muscleGroupIds && <span className={styles.multiSelectError}>{errors.muscleGroupIds}</span>}
                    </div>

                    <FormSelect
                      id="exercise-resistance-type"
                      name="resistanceTypeId"
                      label="Tipo de resistência"
                      value={formValues.resistanceTypeId}
                      onChange={handleInputChange}
                      options={resistanceTypeOptions}
                      placeholder={isLoadingLookups ? 'Carregando tipos...' : 'Selecionar tipo de resistência'}
                      disabled={isReadOnly || isLoadingLookups}
                    />
                  </div>
                </SectionBlock>

                <SectionBlock spacing="margin">
                  <SectionTitle text="Informações complementares" />
                  <TwoColumnGrid>
                    <FormTextarea
                      id="exercise-objective"
                      name="objective"
                      label="Objetivo"
                      value={formValues.objective}
                      onChange={handleInputChange}
                      placeholder="Qual o objetivo principal desse exercício?"
                      rows={4}
                      readOnly={isReadOnly}
                    />

                    <FormTextarea
                      id="exercise-description"
                      name="description"
                      label="Descrição / Orientação"
                      value={formValues.description}
                      onChange={handleInputChange}
                      placeholder="Insira uma descrição ou orientação detalhada sobre a execução do exercício."
                      rows={4}
                      readOnly={isReadOnly}
                    />
                  </TwoColumnGrid>
                </SectionBlock>

                <SectionBlock spacing="margin">
                  <SectionTitle text="Execução" />
                  <div className={styles.executionGridStack}>
                    <div className={styles.threeColumnGrid}>
                      <FormInput
                        id="exercise-sets"
                        name="sets"
                        label="Séries"
                        value={formValues.sets}
                        onChange={handleInputChange}
                        placeholder="Ex: 3"
                        readOnly={isReadOnly}
                      />

                      <div className={styles.repetitionRangeField}>
                        <span className={styles.repetitionRangeLabel}>Repetições</span>
                        <div className={styles.repetitionRangeInputs}>
                          <FormInput
                            id="exercise-repetitions-min"
                            name="repetitionsMin"
                            value={formValues.repetitionsMin}
                            onChange={handleInputChange}
                            placeholder="Mínimo"
                            aria-label="Repetições mínimas"
                            inputMode="numeric"
                            maxLength={5}
                            error={errors.repetitionsMin}
                            containerClassName={styles.repetitionRangeInput}
                            readOnly={isReadOnly}
                          />

                          <span className={styles.repetitionRangeSeparator}>até</span>

                          <FormInput
                            id="exercise-repetitions-max"
                            name="repetitionsMax"
                            value={formValues.repetitionsMax}
                            onChange={handleInputChange}
                            placeholder="Máximo"
                            aria-label="Repetições máximas"
                            inputMode="numeric"
                            maxLength={5}
                            error={errors.repetitionsMax}
                            containerClassName={styles.repetitionRangeInput}
                            readOnly={isReadOnly}
                          />
                        </div>
                      </div>

                      <FormSelect
                        id="exercise-method"
                        name="method"
                        label="Método"
                        value={formValues.method}
                        onChange={handleInputChange}
                        options={methodOptions}
                        triggerFormat={renderMethodOption}
                        optionFormat={renderMethodOption}
                        placeholder="Selecionar"
                        disabled={isReadOnly || isLoadingLookups}
                      />
                    </div>

                    <div className={styles.threeColumnGrid}>
                      {usesIsometrySpeed ? (
                        <DurationPickerField
                          id="exercise-isometric-time"
                          label="Tempo"
                          value={formValues.executionTime}
                          onChange={(value) => handleSecondsFieldChange('executionTime', value)}
                          error={errors.executionTime}
                          placeholder="Configurar tempo"
                          defaultSeconds={DEFAULT_ISOMETRIC_SECONDS}
                          disabled={isReadOnly}
                        />
                      ) : usesCadenceSpeed ? null : (
                        <FormInput
                          id="exercise-execution-time"
                          name="executionTime"
                          label="Execução"
                          value={formValues.executionTime}
                          onChange={handleInputChange}
                          placeholder="Ex: 30"
                          inputMode="numeric"
                          maxLength={5}
                          endAdornment={<span className={styles.inputAdornment}>s</span>}
                          readOnly={isReadOnly}
                        />
                      )}

                      <DurationPickerField
                        id="exercise-rest-time"
                        label="Descanso"
                        value={formValues.restTime}
                        onChange={handleRestTimeChange}
                        placeholder="Configurar intervalo"
                        defaultSeconds={DEFAULT_REST_SECONDS}
                        disabled={isReadOnly}
                      />

                      <FormSelect
                        id="exercise-execution-speed"
                        name="executionSpeed"
                        label="Velocidade de execução"
                        value={formValues.executionSpeed}
                        onChange={handleInputChange}
                        options={EXERCISE_EXECUTION_SPEED_OPTIONS}
                        placeholder="Selecionar"
                        error={errors.executionSpeed}
                        disabled={isReadOnly}
                      />
                    </div>

                    {usesCadenceSpeed ? (
                      <div className={styles.cadenceTimesRow}>
                        <DurationPickerField
                          id="exercise-concentric-time"
                          label="Concêntrica"
                          value={formValues.concentricSeconds}
                          onChange={(value) => handleSecondsFieldChange('concentricSeconds', value)}
                          error={errors.concentricSeconds}
                          placeholder="Configurar tempo"
                          pickerMode="seconds"
                          defaultSeconds={DEFAULT_CONCENTRIC_SECONDS}
                          allowZero
                          disabled={isReadOnly}
                        />
                        <DurationPickerField
                          id="exercise-pause-time"
                          label="Pausa"
                          value={formValues.pauseSeconds}
                          onChange={(value) => handleSecondsFieldChange('pauseSeconds', value)}
                          error={errors.pauseSeconds}
                          placeholder="Configurar tempo"
                          pickerMode="seconds"
                          defaultSeconds={DEFAULT_PAUSE_SECONDS}
                          allowZero
                          disabled={isReadOnly}
                        />
                        <DurationPickerField
                          id="exercise-eccentric-time"
                          label="Excêntrica"
                          value={formValues.eccentricSeconds}
                          onChange={(value) => handleSecondsFieldChange('eccentricSeconds', value)}
                          error={errors.eccentricSeconds}
                          placeholder="Configurar tempo"
                          pickerMode="seconds"
                          defaultSeconds={DEFAULT_ECCENTRIC_SECONDS}
                          allowZero
                          disabled={isReadOnly}
                        />
                      </div>
                    ) : null}

                    <div className={styles.threeColumnGrid}>
                      {!isBodyweightExercise && (
                        <FormSelect
                          id="exercise-load-percentage"
                          name="loadPercentage"
                          label="% Carga"
                          value={formValues.loadPercentage}
                          onChange={handleInputChange}
                          options={LOAD_OPTIONS}
                          placeholder="Selecionar"
                          error={errors.loadPercentage}
                          disabled={isReadOnly}
                        />
                      )}

                      <FormSelect
                        id="exercise-intensity"
                        name="intensity"
                        label="Intensidade (RPE 0-10)"
                        value={formValues.intensity}
                        onChange={handleInputChange}
                        options={RPE_OPTIONS}
                        placeholder="Selecionar"
                        disabled={isReadOnly}
                      />

                      <FormSelect
                        id="exercise-heart-rate"
                        name="heartRate"
                        label="Frequência cardíaca"
                        value={formValues.heartRate}
                        onChange={handleInputChange}
                        options={HEART_RATE_OPTIONS}
                        placeholder={isHeartRateFieldDisabled ? 'Disponível para cardio' : 'Selecionar'}
                        disabled={isReadOnly || isHeartRateFieldDisabled}
                      />
                    </div>
                  </div>
                </SectionBlock>
              </div>
            </section>
          </div>

          <div className={styles.sideColumn}>
            <section className={styles.panel}>
              <div className={styles.panelHeader}>Fotos e vídeos</div>
              <div className={styles.panelBody}>
                <div className={styles.thumbnailRow}>
                  <div className={styles.thumbnailBlock}>
                    <span className={styles.thumbnailLabel}>Thumbnail</span>
                    <div className={styles.thumbnailPreview}>
                      {thumbnailPreview ? (
                        <img src={thumbnailPreview} alt="Thumbnail do exercício" className={styles.thumbnailImage} />
                      ) : currentExercise ? (
                        <ExerciseThumbnailImage
                          exerciseId={currentExercise.id}
                          exerciseName={currentExercise.name}
                          alt="Thumbnail do exercício"
                          className={styles.thumbnailImage}
                        />
                      ) : (
                        <span className={`material-symbols-outlined ${styles.thumbnailIcon}`}>image</span>
                      )}
                    </div>
                    {errors.thumbnail && <span className={styles.inlineError}>{errors.thumbnail}</span>}
                  </div>

                  <div className={styles.thumbnailAction}>
                    {!isReadOnly && (
                      <PhotoUploadButton className={styles.insertPhotoButton} onClick={handleThumbnailButtonClick}>
                        Inserir foto
                      </PhotoUploadButton>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className={styles.hiddenInput}
                      onChange={handleThumbnailChange}
                    />
                  </div>
                </div>

                <FormInput
                  id="exercise-video-url"
                  name="videoUrl"
                  label="URL do vídeo"
                  required
                  value={formValues.videoUrl}
                  onChange={handleInputChange}
                  placeholder="Inserir URL"
                  error={errors.videoUrl}
                  readOnly={isReadOnly}
                  endAdornment={(
                    !isReadOnly ? (
                      <button
                        type="button"
                        className={styles.videoClipboardButton}
                        onClick={handlePasteVideoUrl}
                        aria-label="Colar URL do vídeo"
                        title="Colar URL do vídeo"
                      >
                        <Icon name="content_copy" size={16} color="var(--color-neutral-icon-gray)" className={styles.videoClipboardIcon} />
                      </button>
                    ) : undefined
                  )}
                />
              </div>
            </section>
            <section className={styles.panel}>
              <div className={styles.panelHeader}>Exercícios relacionados</div>
              <div className={styles.panelBody}>
                <div className={styles.relatedList}>
                  {invalidRelatedExercises.length > 0 && (
                    <AlertBanner
                      variant="warning"
                      message="Existem exercícios relacionados que não pertencem aos grupos musculares selecionados. Ajuste os relacionados para salvar."
                      className={styles.relatedWarningBanner}
                    />
                  )}

                  {relatedExercises.length === 0 ? (
                    <div className={styles.relatedEmptyState}>
                      {formValues.muscleGroupIds.length === 0 ? (
                        <EmptyState message="Informe a Categoria / Grupo Muscular do exercício para habilitar o botão Adicionar e selecionar exercícios relacionados." showImage={false} icon="exercise" iconSize={24} />
                      ) : (
                        <EmptyState message="Nenhum exercício relacionado selecionado. Clique em Adicionar para buscar exercícios do mesmo grupo." showImage={false} icon="exercise" iconSize={24} />
                      )}
                    </div>
                  ) : (
                    relatedExercises.map((exercise) => (
                      <Card key={exercise.id} className={`${styles.relatedCard}${invalidRelatedExercises.some((item) => item.id === exercise.id) ? ` ${styles.relatedCardInvalid}` : ''}`}>
                        <div className={styles.relatedCardContent}>
                          <ExerciseThumbnailImage
                            exerciseId={exercise.id}
                            exerciseName={exercise.name}
                            alt={`Thumbnail do exercício ${exercise.name}`}
                            className={styles.relatedCardImage}
                          />
                          <div className={styles.relatedCardText}>
                            <span className={styles.relatedCardName}>{exercise.name}</span>
                            <span className={styles.relatedCardMeta}>{resolveRelatedExerciseSubtitle(exercise)}</span>
                            {invalidRelatedExercises.some((item) => item.id === exercise.id) && (
                              <span className={styles.relatedCardWarning}>Fora dos grupos selecionados</span>
                            )}
                          </div>
                          {canManage && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              icon="delete"
                              aria-label={`Remover ${exercise.name}`}
                              onClick={() => handleRequestRemoveRelatedExercise(exercise)}
                              className={styles.relatedRemoveButton}
                            />
                          )}
                        </div>
                      </Card>
                    ))
                  )}
                </div>

                {canManage && (
                  <Button
                    variant="ghost"
                    size="small"
                    className={styles.addRelatedButton}
                    onClick={handleOpenRelatedPicker}
                    type="button"
                    disabled={formValues.muscleGroupIds.length === 0}
                    title={formValues.muscleGroupIds.length === 0 ? 'Selecione ao menos um grupo muscular para adicionar relacionados' : 'Adicionar exercícios relacionados'}
                  >
                    Adicionar
                  </Button>
                )}

                {errors.relatedExercises && <span className={styles.inlineError}>{errors.relatedExercises}</span>}
              </div>
            </section>
          </div>
        </ProfileColumns>
      </ContentContainer>

      <RelatedExercisePickerModal
        open={canManage && isRelatedPickerOpen}
        exercises={relatedExerciseOptions}
        loading={relatedCatalogLoading}
        searchTerm={relatedSearchTerm}
        fixedGroupLabels={selectedMuscleGroupLabels}
        selectedIds={pendingRelatedExercises.map((exercise) => exercise.id)}
        onClose={handleCloseRelatedPicker}
        onConfirm={handleConfirmRelatedSelection}
        onSearchChange={handleRelatedSearchChange}
        onToggleSelect={handleTogglePendingRelated}
      />

      <ConfirmModal
        open={canManage && Boolean(relatedExercisePendingRemoval)}
        title="Remover exercício relacionado"
        message={relatedExercisePendingRemoval ? `Deseja remover ${relatedExercisePendingRemoval.name} dos exercícios relacionados?` : ''}
        confirmLabel="Sim"
        cancelLabel="Não"
        variant="warning"
        onCancel={handleCancelRemoveRelatedExercise}
        onConfirm={() => {
          if (relatedExercisePendingRemoval) {
            handleConfirmRemoveRelatedExercise(relatedExercisePendingRemoval.id);
          }
        }}
      />
    </PageContainer>
  );
};

export default ExerciseRegister;